import { useSnackbar } from 'notistack';
import { useRef } from 'react';
import { useQueryClient } from 'react-query';
import { REPRESENTATIVES_QUERY_STRING } from 'shared/constants/REPRESENTATIVES_QUERY_STRING';
import {
  IRepresentativeProfilesTalentLevelResumeCategories,
  IRepresentativesResumePayloadResponse,
} from 'shared/interfaces/IRepresentativesEditProfile';
import { profileService } from 'shared/services/profileService';
import { useDebouncedCallback } from 'use-debounce';
import { useEditProfileMedia } from '../context/EditProfileMediaProvider';
import { AgencyPermissionEnums } from 'shared/enums/AgencyPermissionEnums';

const { updateProfile } = profileService();

const err_msg = 'An error occurred while processing your request. Your previous sorting has been restored.';

export const useEditProfileMediaResumeUpdater = (
  profileId: string,
  attachedResume: IRepresentativeProfilesTalentLevelResumeCategories[],
) => {
  const { handleAction } = useEditProfileMedia();
  const representative_resume_key = [REPRESENTATIVES_QUERY_STRING.representatives_single_profile_resume, profileId];
  const queryClient = useQueryClient();
  const hideQueRef = useRef<Set<string>>(new Set());
  const unHideQueRef = useRef<Set<string>>(new Set());
  const { mutateAsync: updateProfileMutateAsync } = updateProfile();
  const { enqueueSnackbar } = useSnackbar();

  const HideDebounceMutation = useDebouncedCallback(() => {
    const payload: string[] = Array.from(hideQueRef.current);
    const filteredAttachedResume = attachedResume
      ?.filter((attached) => !payload.includes(attached.category_id))
      ?.map((resume) => resume.category_id);
    updateProfileMutateAsync(
      {
        profileId,
        payload: {
          resume: filteredAttachedResume,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(['id', profileId], {
            refetchInactive: true,
          });
          await queryClient.invalidateQueries(
            [REPRESENTATIVES_QUERY_STRING.representatives_single_profile, profileId],
            {
              refetchInactive: true,
            },
          );
          await queryClient.resetQueries([REPRESENTATIVES_QUERY_STRING.representatives_multiple_profile]);
        },
        onError: async () => {
          enqueueSnackbar(err_msg, {
            variant: 'error',
          });
          queryClient.setQueryData(representative_resume_key, (oldData: any) => {
            const prevData: IRepresentativesResumePayloadResponse = oldData;

            return {
              ...prevData,
              data: {
                ...prevData?.data,
                profile: {
                  ...prevData?.data?.profile,
                  resume_category_ids: [...(prevData?.data?.profile?.resume_category_ids || []), ...payload],
                },
              },
            };
          });
        },
        onSettled: () => {
          hideQueRef.current.clear();
        },
      },
    );
  }, 1000);

  const onHideClick = (resume_id: string) => {
    handleAction(AgencyPermissionEnums.RESUME_CATEGORIES, () => {
      queryClient.setQueryData(representative_resume_key, (oldData: any) => {
        const previousData: IRepresentativesResumePayloadResponse = oldData;

        return {
          ...previousData,
          data: {
            ...previousData?.data,
            profile: {
              ...previousData?.data?.profile,
              resume_category_ids: previousData?.data?.profile?.resume_category_ids?.filter((id) => id !== resume_id),
            },
          },
        };
      });

      hideQueRef.current.add(resume_id);

      HideDebounceMutation();
    });
  };

  const UnHideDebounceMutation = useDebouncedCallback(() => {
    const payload: string[] = Array.from(unHideQueRef.current);
    const filteredAttachedResume = attachedResume
      ?.filter((attached) => !payload.includes(attached.category_id))
      ?.map((resume) => resume.category_id);
    unHideQueRef.current.clear();

    updateProfileMutateAsync(
      {
        profileId,
        payload: {
          resume: [...filteredAttachedResume, ...payload],
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(['id', profileId], {
            refetchInactive: true,
          });
          await queryClient.invalidateQueries(
            [REPRESENTATIVES_QUERY_STRING.representatives_single_profile, profileId],
            {
              refetchInactive: true,
            },
          );
          await queryClient.resetQueries([REPRESENTATIVES_QUERY_STRING.representatives_multiple_profile]);
        },
        onError: async () => {
          enqueueSnackbar(err_msg, {
            variant: 'error',
          });
          // const dataRevert =
          queryClient.setQueryData(representative_resume_key, (oldData: any) => {
            const prevData: IRepresentativesResumePayloadResponse = oldData;

            return {
              ...prevData,
              data: {
                ...prevData?.data,
                profile: {
                  ...prevData?.data?.profile,
                  resume_category_ids: prevData?.data?.profile?.resume_category_ids?.filter(
                    (category) => !payload.includes(category),
                  ),
                },
              },
            };
          });
        },
      },
    );
  }, 1000);

  const onUnHideClick = (resume_id: string) => {
    handleAction(AgencyPermissionEnums.RESUME_CATEGORIES, () => {
      unHideQueRef.current.add(resume_id);

      queryClient.setQueryData(representative_resume_key, (oldData: any) => {
        const previousData: IRepresentativesResumePayloadResponse = oldData;

        return {
          ...previousData,
          data: {
            ...previousData?.data,
            profile: {
              ...previousData?.data?.profile,
              resume_category_ids: [...(previousData?.data?.profile?.resume_category_ids || []), resume_id],
            },
          },
        };
      });
      UnHideDebounceMutation();
    });
  };

  return { onHideClick, onUnHideClick };
};
